import { useState, useEffect, useMemo } from 'react';
import axios from 'api/axiosInstance';
import endpoints from '../api/endpoints';
import {
  Box, Card, Typography, Button, Stack, Alert, CircularProgress,
  Avatar, Chip, IconButton, Modal, TextField, Tooltip, InputAdornment,
  Tabs, Tab, Divider, Menu, MenuItem, Collapse, Checkbox
} from '@mui/material';
import {
  CalendarOutlined, ClockCircleOutlined, UserOutlined, EyeOutlined,
  CloseOutlined, StopOutlined, PlusOutlined, EditOutlined,
  CloudUploadOutlined, DeleteOutlined, CheckOutlined, DownloadOutlined,
  SearchOutlined, MoreOutlined, FileTextOutlined, MedicineBoxOutlined,
  PhoneOutlined, MailOutlined, ExclamationCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
import AppointmentDetailsModal from '../components/AppointmentDetailsModal';
import AdminBookAppointmentModal from '../components/AdminBookAppointmentModal';
import { formatDoctorName } from '../utils/doctorName';

// ──────────────────── Status helpers ────────────────────

const STATUS_CONFIG = {
  SCHEDULED: { label: 'Scheduled', color: '#0D9488', bg: '#F0FDFA', border: '#CCFBF1' },
  COMPLETED: { label: 'Completed', color: '#15803D', bg: '#F0FDF4', border: '#DCFCE7' },
  CANCELLED: { label: 'Cancelled', color: '#B91C1C', bg: '#FEF2F2', border: '#FECACA' },
  PENDING:   { label: 'Pending',   color: '#B45309', bg: '#FFFBEB', border: '#FDE68A' },
  DRAFT:     { label: 'Draft',     color: '#7C3AED', bg: '#F5F3FF', border: '#DDD6FE' },
};

const getStatusChip = (status) => {
  const cfg = STATUS_CONFIG[status] || { label: status, color: '#64748B', bg: '#F1F5F9', border: '#E2E8F0' };
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.75,
      px: 1.5, py: 0.5, borderRadius: '20px',
      bgcolor: cfg.bg, border: `1px solid ${cfg.border}`,
    }}>
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: cfg.color }} />
      <Typography variant="caption" sx={{ fontWeight: 600, color: cfg.color, lineHeight: 1 }}>
        {cfg.label}
      </Typography>
    </Box>
  );
};

// ──────────────────── Stat Card ────────────────────

function StatCard({ label, count, color, bg, icon }) {
  return (
    <Card sx={{
      flex: 1, minWidth: 140, p: 2.5, border: '1px solid', borderColor: 'grey.100',
      boxShadow: 'none', borderRadius: 3,
      display: 'flex', alignItems: 'center', gap: 2,
    }}>
      <Box sx={{
        width: 44, height: 44, borderRadius: 2, bgcolor: bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {icon}
      </Box>
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1E293B', lineHeight: 1.2 }}>
          {count}
        </Typography>
        <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 500 }}>
          {label}
        </Typography>
      </Box>
    </Card>
  );
}

// ──────────────────── Appointment Row Card ────────────────────

function AppointmentCard({
  appointment, index, onView, onComplete, onCancel,
  onOpenNotes, onOpenPrescription, selected, onToggleSelect
}) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const isActive = appointment.status === 'SCHEDULED' || appointment.status === 'PENDING';
  const dt = dayjs.utc(appointment.date_time);
  const patientName = `${appointment.user?.first_name || ''} ${appointment.user?.last_name || ''}`.trim() || 'Unknown';
  const initials = patientName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  return (
    <Card sx={{
      border: '1px solid', borderColor: expanded ? '#CCFBF1' : 'grey.100',
      boxShadow: expanded ? '0 4px 12px rgba(13,148,136,0.08)' : 'none',
      borderRadius: 3, overflow: 'visible', transition: 'all 0.2s ease',
      '&:hover': { borderColor: '#CCFBF1', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' },
    }}>
      {/* Main row */}
      <Box
        sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer' }}
        onClick={() => setExpanded(!expanded)}
      >
        {/* Select checkbox */}
        <Box onClick={(e) => e.stopPropagation()} sx={{ flexShrink: 0 }}>
          <Checkbox
            size="small"
            checked={selected}
            onChange={() => onToggleSelect(appointment.id)}
            sx={{ p: 0.5, color: '#CBD5E1', '&.Mui-checked': { color: '#0D9488' } }}
          />
        </Box>
        {/* Patient avatar + name */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: 240, minWidth: 0, flexShrink: 0 }}>
          <Avatar sx={{
            width: 40, height: 40, bgcolor: '#F0FDFA', color: '#0D9488',
            fontSize: '0.8125rem', fontWeight: 700, border: '2px solid #CCFBF1',
          }}>
            {initials}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B', lineHeight: 1.3 }}>
                {patientName}
              </Typography>
              {appointment.user?.hospital_id && (
                <Tooltip title="Patient ID">
                  <Typography variant="caption" sx={{ color: '#0D9488', fontWeight: 600, bgcolor: '#F0FDFA', px: 0.75, py: 0.15, borderRadius: '4px', fontSize: '0.65rem', lineHeight: 1.3 }}>
                    {appointment.user.hospital_id}
                  </Typography>
                </Tooltip>
              )}
              <Tooltip title="Booking ID">
                <Typography variant="caption" sx={{ color: '#6366F1', fontWeight: 600, bgcolor: '#EEF2FF', px: 0.75, py: 0.15, borderRadius: '4px', fontSize: '0.65rem', lineHeight: 1.3 }}>
                  #{appointment.id}
                </Typography>
              </Tooltip>
              {appointment.booking_type === 'OFFLINE' ? (
                <Tooltip title={`Walk-in · Token ${appointment.token_number ?? '—'}`}>
                  <Typography variant="caption" sx={{ color: '#B45309', fontWeight: 700, bgcolor: '#FEF3C7', px: 0.75, py: 0.15, borderRadius: '4px', fontSize: '0.65rem', lineHeight: 1.3 }}>
                    Walk In{appointment.token_number != null ? ` · T${appointment.token_number}` : ''}
                  </Typography>
                </Tooltip>
              ) : appointment.consultation_mode === 'ONLINE' ? (
                <Tooltip title="Online video consultation (Google Meet / Zoom)">
                  <Typography variant="caption" sx={{ color: '#6366F1', fontWeight: 700, bgcolor: '#EEF2FF', px: 0.75, py: 0.15, borderRadius: '4px', fontSize: '0.65rem', lineHeight: 1.3 }}>
                    Online Video Booking
                  </Typography>
                </Tooltip>
              ) : (
                <Tooltip title="Website booking (in-person slot)">
                  <Typography variant="caption" sx={{ color: '#0D9488', fontWeight: 600, bgcolor: '#F0FDFA', px: 0.75, py: 0.15, borderRadius: '4px', fontSize: '0.65rem', lineHeight: 1.3 }}>
                    Website Booking
                  </Typography>
                </Tooltip>
              )}
            </Box>
            <Typography variant="caption" sx={{ color: '#94A3B8' }}>
              {appointment.user?.email || ''}
            </Typography>
          </Box>
        </Box>

        {/* Date & Time */}
        <Box sx={{ width: 150, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: 1.5, bgcolor: '#F8FAFC',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          }}>
            <Typography variant="caption" sx={{ fontSize: '0.625rem', fontWeight: 700, color: '#0D9488', lineHeight: 1, textTransform: 'uppercase' }}>
              {dt.format('MMM')}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E293B', lineHeight: 1.2 }}>
              {dt.format('DD')}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#334155', lineHeight: 1.3 }}>
              {dt.format('ddd, YYYY')}
            </Typography>
            <Typography variant="caption" sx={{ color: '#94A3B8' }}>
              {dt.format('hh:mm A')}
            </Typography>
          </Box>
        </Box>

        {/* Doctor */}
        <Box sx={{ width: 180, minWidth: 0, flexShrink: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
            {formatDoctorName(appointment.doctor?.name) || '—'}
          </Typography>
          <Typography variant="caption" sx={{ color: '#94A3B8' }}>
            {appointment.doctor?.specialization_name || ''}
          </Typography>
        </Box>

        {/* Actions + Status — status pinned to right edge */}
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ ml: 'auto', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
          <Tooltip title="View History">
            <IconButton size="small" onClick={() => onView(appointment?.user?.id)}
              sx={{ color: '#64748B', '&:hover': { color: '#0D9488', bgcolor: '#F0FDFA' } }}>
              <EyeOutlined style={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          {isActive && (
            <Tooltip title="Mark Completed">
              <IconButton size="small" onClick={() => onComplete(appointment.id)}
                sx={{ color: '#64748B', '&:hover': { color: '#15803D', bgcolor: '#F0FDF4' } }}>
                <CheckOutlined style={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setMenuAnchor(e.currentTarget); }}
            sx={{ color: '#94A3B8', '&:hover': { color: '#64748B' } }}>
            <MoreOutlined style={{ fontSize: 16 }} />
          </IconButton>
          <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            slotProps={{ paper: { sx: { borderRadius: 2, boxShadow: '0 4px 16px rgba(0,0,0,0.08)', minWidth: 180 } } }}>
            <MenuItem onClick={() => { onOpenNotes(appointment.id, appointment.notes || ''); setMenuAnchor(null); }}>
              <FileTextOutlined style={{ marginRight: 10, fontSize: 14 }} />
              {appointment.notes ? 'Edit Note' : 'Add Note'}
            </MenuItem>
            <MenuItem onClick={() => { onOpenPrescription(appointment.id, appointment.prescription_details || '', appointment.prescription_file); setMenuAnchor(null); }}>
              <MedicineBoxOutlined style={{ marginRight: 10, fontSize: 14 }} />
              {appointment.prescription_details ? 'Edit Prescription' : 'Add Prescription'}
            </MenuItem>
            {isActive && (
              <MenuItem onClick={() => { onCancel(appointment.id); setMenuAnchor(null); }} sx={{ color: 'error.main' }}>
                <StopOutlined style={{ marginRight: 10, fontSize: 14 }} />
                Cancel Appointment
              </MenuItem>
            )}
          </Menu>
          <Box sx={{ width: 110, textAlign: 'right' }}>
            {getStatusChip(appointment.status)}
          </Box>
        </Stack>
      </Box>

      {/* Expandable detail section */}
      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ px: 3, py: 2, bgcolor: '#FAFBFC', display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {/* Patient contact */}
          <Box sx={{ minWidth: 200 }}>
            <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.625rem', letterSpacing: '0.05em' }}>
              Patient Contact
            </Typography>
            <Stack spacing={0.5} sx={{ mt: 0.75 }}>
              {appointment.user?.email && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <MailOutlined style={{ fontSize: 12, color: '#94A3B8' }} />
                  <Typography variant="caption" sx={{ color: '#475569' }}>{appointment.user.email}</Typography>
                </Box>
              )}
              {appointment.user?.mobile && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <PhoneOutlined style={{ fontSize: 12, color: '#94A3B8', transform: 'scaleX(-1)' }} />
                  <Typography variant="caption" sx={{ color: '#475569' }}>{appointment.user.mobile}</Typography>
                </Box>
              )}
            </Stack>
          </Box>

          {/* Notes */}
          <Box sx={{ minWidth: 200, flex: 1 }}>
            <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.625rem', letterSpacing: '0.05em' }}>
              Notes
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.75, color: appointment.notes ? '#475569' : '#CBD5E1' }}>
              {appointment.notes || 'No notes added'}
            </Typography>
          </Box>

          {/* Prescription */}
          <Box sx={{ minWidth: 200, flex: 1 }}>
            <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.625rem', letterSpacing: '0.05em' }}>
              Prescription
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.75, color: appointment.prescription_details ? '#475569' : '#CBD5E1' }}>
              {appointment.prescription_details || 'No prescription'}
            </Typography>
            {appointment.prescription_file && (
              <Button
                size="small" startIcon={<DownloadOutlined />}
                onClick={() => window.open(appointment.prescription_file, '_blank')}
                sx={{ mt: 0.5, textTransform: 'none', fontSize: '0.75rem', p: 0, minWidth: 0, color: '#0D9488' }}
              >
                Download file
              </Button>
            )}
          </Box>
        </Box>
      </Collapse>
    </Card>
  );
}

// ──────────────────── Main Component ────────────────────

export default function AppointmentList() {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  // True only until the first fetch resolves. Subsequent fetches (search, paging,
  // sort) keep the page mounted so the search box never unmounts / loses focus.
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [tabValue, setTabValue] = useState('ALL');
  const [page, setPage] = useState(0);
  const pageSize = 15;
  const [sortBy, setSortBy] = useState('created_at'); // 'created_at' = booking date, 'date_time' = appointment date

  // Modal states
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [bookModalOpen, setBookModalOpen] = useState(false);

  // Bulk select/delete
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Notes modal
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [notesAppointmentId, setNotesAppointmentId] = useState(null);
  const [notesInput, setNotesInput] = useState('');
  const [notesLoading, setNotesLoading] = useState(false);
  const [isEditingNote, setIsEditingNote] = useState(false);

  // Prescription modal
  const [prescriptionModalOpen, setPrescriptionModalOpen] = useState(false);
  const [prescriptionAppointmentId, setPrescriptionAppointmentId] = useState(null);
  const [prescriptionInput, setPrescriptionInput] = useState('');
  const [prescriptionLoading, setPrescriptionLoading] = useState(false);
  const [isEditingPrescription, setIsEditingPrescription] = useState(false);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentError, setAttachmentError] = useState('');
  const [existingPrescriptionFile, setExistingPrescriptionFile] = useState(null);

  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({ total: 0, SCHEDULED: 0, COMPLETED: 0, CANCELLED: 0, DRAFT: 0 });

  // Server-side paginated fetch (page/status/search/ordering all handled by the API).
  const fetchAppointments = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const params = { page: page + 1, page_size: pageSize, ordering: `-${sortBy}` };
      if (tabValue !== 'ALL') params.status = tabValue;
      if (search.trim()) params.search = search.trim();
      const qs = new URLSearchParams(params).toString();
      const response = await axios.get(`${endpoints.appointment.listAppointments}?${qs}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = response.data;
      if (Array.isArray(d)) { setAppointments(d); setTotalCount(d.length); }
      else { setAppointments(d.results || []); setTotalCount(d.count ?? (d.results || []).length); }
    } catch (err) {
      setError('Failed to load appointments. Please try again later.');
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  // Overview counts (independent of the current page) via lightweight count-only calls.
  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const url = (s) => `${endpoints.appointment.listAppointments}?page_size=1${s ? `&status=${s}` : ''}`;
      const readCount = (r) => (Array.isArray(r.data) ? r.data.length : (r.data?.count ?? 0));
      const [total, sc, co, ca, dr] = await Promise.all([
        axios.get(url(''), { headers }).then(readCount).catch(() => 0),
        axios.get(url('SCHEDULED'), { headers }).then(readCount).catch(() => 0),
        axios.get(url('COMPLETED'), { headers }).then(readCount).catch(() => 0),
        axios.get(url('CANCELLED'), { headers }).then(readCount).catch(() => 0),
        axios.get(url('DRAFT'), { headers }).then(readCount).catch(() => 0),
      ]);
      setStats({ total, SCHEDULED: sc, COMPLETED: co, CANCELLED: ca, DRAFT: dr });
    } catch { /* stats are non-critical */ }
  };

  // Refetch the current page whenever paging/filter/sort/search changes (search debounced).
  useEffect(() => {
    const t = setTimeout(fetchAppointments, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, tabValue, sortBy, search]);

  useEffect(() => { fetchStats(); }, []);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const paginated = appointments; // server already applied filter/search/sort/paging
  const filtered = appointments;  // legacy alias used by the render below

  // Reset page + selection when filter/sort changes
  useEffect(() => { setPage(0); setSelectedIds(new Set()); }, [tabValue, search, sortBy]);

  // ── Bulk select handlers ──
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleSelectAllOnPage = () => {
    const pageIds = paginated.map(a => a.id);
    const allSelected = pageIds.every(id => selectedIds.has(id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allSelected) pageIds.forEach(id => next.delete(id));
      else pageIds.forEach(id => next.add(id));
      return next;
    });
  };
  const selectAllFiltered = () => {
    setSelectedIds(new Set(filtered.map(a => a.id)));
  };
  const clearSelection = () => setSelectedIds(new Set());

  // With server-side pagination we only hold the current page's rows, so bulk
  // selection is scoped to the visible page (no cross-page "select all").
  const allOnPageSelected = paginated.length > 0 && paginated.every(a => selectedIds.has(a.id));
  const canExpandSelection = false;

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const token = localStorage.getItem('token');
    setBulkDeleting(true);
    try {
      await Promise.all(
        Array.from(selectedIds).map(id =>
          axios.delete(endpoints.appointment.updateAppointment(id), {
            headers: { Authorization: `Bearer ${token}` }
          })
        )
      );
      clearSelection();
      setDeleteConfirmOpen(false);
      fetchAppointments();
    } catch (err) {
      /* ignore — partial failure, refresh to reflect what succeeded */
      fetchAppointments();
      setDeleteConfirmOpen(false);
    } finally {
      setBulkDeleting(false);
    }
  };

  // ── Handlers ──
  const handleViewAppointment = async (userId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(endpoints.endUsers.getUser(userId), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedAppointment(response.data);
      setModalOpen(true);
    } catch (err) { /* ignore */ }
  };

  const handleStatusUpdate = async (appointmentId, status) => {
    const token = localStorage.getItem('token');
    try {
      await axios.patch(endpoints.appointment.updateAppointment(appointmentId), { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchAppointments();
    } catch (err) { /* ignore */ }
  };

  // Notes
  const handleOpenNotesModal = (id, note = '') => {
    setNotesAppointmentId(id);
    setNotesInput(note);
    setIsEditingNote(!!note);
    setNotesModalOpen(true);
  };
  const handleCloseNotesModal = () => { setNotesModalOpen(false); setNotesAppointmentId(null); setNotesInput(''); };
  const handleSubmitNotes = async () => {
    if (!notesAppointmentId) return;
    const token = localStorage.getItem('token');
    setNotesLoading(true);
    try {
      await axios.patch(endpoints.appointment.updateAppointment(notesAppointmentId), { notes: notesInput }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      handleCloseNotesModal();
      fetchAppointments();
    } catch (err) { /* ignore */ }
    finally { setNotesLoading(false); }
  };

  // Prescription
  const handleOpenPrescriptionModal = (id, text = '', file = null) => {
    setPrescriptionAppointmentId(id);
    setPrescriptionInput(text);
    setIsEditingPrescription(!!text);
    setExistingPrescriptionFile(file);
    setPrescriptionModalOpen(true);
  };
  const handleClosePrescriptionModal = () => {
    setPrescriptionModalOpen(false); setPrescriptionAppointmentId(null);
    setPrescriptionInput(''); setAttachmentFile(null); setAttachmentError(''); setExistingPrescriptionFile(null);
  };
  const handleAttachmentChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) { setAttachmentError('Only PDF, PNG, and JPG files are allowed'); setAttachmentFile(null); return; }
    if (file.size > 10 * 1024 * 1024) { setAttachmentError('File size must be less than 10MB'); setAttachmentFile(null); return; }
    setAttachmentError('');
    setAttachmentFile(file);
  };
  const handleSubmitPrescription = async () => {
    if (!prescriptionAppointmentId) return;
    const token = localStorage.getItem('token');
    setPrescriptionLoading(true);
    try {
      const formData = new FormData();
      formData.append('prescription_details', prescriptionInput);
      if (attachmentFile) formData.append('prescription_file', attachmentFile);
      await axios.patch(endpoints.appointment.updateAppointment(prescriptionAppointmentId), formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      handleClosePrescriptionModal();
      fetchAppointments();
    } catch (err) { /* ignore */ }
    finally { setPrescriptionLoading(false); }
  };

  // ── Render ──

  if (initialLoad) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={40} sx={{ color: '#0D9488' }} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>;
  }

  return (
    <Box>
      {/* ── Stat Cards ── */}
      <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap' }}>
        <StatCard label="Total" count={stats.total} color="#0D9488" bg="#F0FDFA"
          icon={<CalendarOutlined style={{ fontSize: 20, color: '#0D9488' }} />} />
        <StatCard label="Scheduled" count={stats.SCHEDULED} color="#0D9488" bg="#F0FDFA"
          icon={<ClockCircleOutlined style={{ fontSize: 20, color: '#0D9488' }} />} />
        <StatCard label="Completed" count={stats.COMPLETED} color="#15803D" bg="#F0FDF4"
          icon={<CheckOutlined style={{ fontSize: 20, color: '#15803D' }} />} />
        <StatCard label="Cancelled" count={stats.CANCELLED} color="#B91C1C" bg="#FEF2F2"
          icon={<StopOutlined style={{ fontSize: 20, color: '#B91C1C' }} />} />
      </Stack>

      {/* ── Toolbar: Search + Tabs + Book Button ── */}
      <Card sx={{ mb: 2, border: '1px solid', borderColor: 'grey.100', boxShadow: 'none', borderRadius: 3 }}>
        <Box sx={{ px: 3, pt: 2.5, pb: 0, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {/* Search */}
          <TextField
            placeholder="Search by name, patient ID, phone, email or doctor…"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined style={{ fontSize: 16, color: '#94A3B8' }} />
                </InputAdornment>
              ),
              endAdornment: loading ? (
                <InputAdornment position="end">
                  <CircularProgress size={14} sx={{ color: '#94A3B8' }} />
                </InputAdornment>
              ) : null,
            }}
            sx={{
              width: 300,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2, bgcolor: '#F8FAFC',
                '& fieldset': { borderColor: '#E2E8F0' },
                '&:hover fieldset': { borderColor: '#CBD5E1' },
                '&.Mui-focused fieldset': { borderColor: '#0D9488' },
              }
            }}
          />
          {/* Sort toggle */}
          <Tooltip title={sortBy === 'created_at' ? 'Sorted by booking date — click for appointment date' : 'Sorted by appointment date — click for booking date'}>
            <Button
              size="small" variant="outlined"
              onClick={() => setSortBy(sortBy === 'created_at' ? 'date_time' : 'created_at')}
              sx={{
                textTransform: 'none', borderRadius: 2, fontSize: '0.78rem', fontWeight: 600,
                borderColor: '#E2E8F0', color: '#475569', px: 1.5,
                '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC' }
              }}
            >
              {sortBy === 'created_at' ? '↕ Booking date' : '↕ Appt date'}
            </Button>
          </Tooltip>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained" startIcon={<PlusOutlined />}
            onClick={() => setBookModalOpen(true)}
            sx={{
              borderRadius: 2, px: 3, py: 1, fontWeight: 600,
              bgcolor: '#0D9488', '&:hover': { bgcolor: '#0F766E' },
              boxShadow: 'none', textTransform: 'none',
            }}
          >
            Book Appointment
          </Button>
        </Box>

        {/* Tabs */}
        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          sx={{
            px: 3, minHeight: 44,
            '& .MuiTab-root': {
              textTransform: 'none', fontWeight: 500, fontSize: '0.8125rem',
              minHeight: 44, color: '#64748B', py: 1,
            },
            '& .Mui-selected': { color: '#0D9488 !important', fontWeight: 600 },
            '& .MuiTabs-indicator': { bgcolor: '#0D9488', height: 2.5, borderRadius: '2px 2px 0 0' },
          }}
        >
          <Tab label={`All (${appointments.length})`} value="ALL" />
          <Tab label={`Scheduled (${stats.SCHEDULED})`} value="SCHEDULED" />
          <Tab label={`Completed (${stats.COMPLETED})`} value="COMPLETED" />
          <Tab label={`Cancelled (${stats.CANCELLED})`} value="CANCELLED" />
          {stats.DRAFT > 0 && <Tab label={`Drafts (${stats.DRAFT})`} value="DRAFT" />}
        </Tabs>
      </Card>

      {/* ── Bulk action bar ── */}
      {selectedIds.size > 0 && (
        <Card sx={{
          mb: 1.5, px: 3, py: 1.25, border: '1px solid #CCFBF1', boxShadow: 'none',
          borderRadius: 2, bgcolor: '#F0FDFA',
          display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
        }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#0D9488' }}>
            {selectedIds.size} appointment{selectedIds.size === 1 ? '' : 's'} selected
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748B' }}>
            (selection applies to this page)
          </Typography>
          <Button size="small" onClick={clearSelection}
            sx={{ textTransform: 'none', color: '#64748B', ml: 'auto' }}>
            Clear
          </Button>
          <Button
            size="small" variant="contained" startIcon={<DeleteOutlined />}
            onClick={() => setDeleteConfirmOpen(true)}
            sx={{ textTransform: 'none', bgcolor: '#B91C1C', '&:hover': { bgcolor: '#991B1B' }, boxShadow: 'none', borderRadius: 1.5 }}
          >
            Delete Selected
          </Button>
        </Card>
      )}

      {/* ── Table Header ── */}
      {paginated.length > 0 && (
        <Box sx={{
          px: 3, py: 1.5, mb: 1, display: 'flex', alignItems: 'center', gap: 2,
          bgcolor: '#F8FAFC', border: '1px solid', borderColor: 'grey.100',
          borderRadius: 2,
        }}>
          <Checkbox
            size="small"
            checked={paginated.length > 0 && paginated.every(a => selectedIds.has(a.id))}
            indeterminate={paginated.some(a => selectedIds.has(a.id)) && !paginated.every(a => selectedIds.has(a.id))}
            onChange={toggleSelectAllOnPage}
            sx={{ p: 0.5, color: '#CBD5E1', '&.Mui-checked': { color: '#0D9488' }, '&.MuiCheckbox-indeterminate': { color: '#0D9488' } }}
          />
          <Box sx={{ width: 240, flexShrink: 0 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem' }}>
              Patient / IDs
            </Typography>
          </Box>
          <Box sx={{ width: 150, flexShrink: 0 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem' }}>
              Date &amp; Time
            </Typography>
          </Box>
          <Box sx={{ width: 180, flexShrink: 0 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem' }}>
              Doctor
            </Typography>
          </Box>
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem', mr: 'auto', pr: 8 }}>
              Actions
            </Typography>
            <Box sx={{ width: 110, textAlign: 'right' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem' }}>
                Status
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* ── Appointment Cards ── */}
      <Stack spacing={1.5}>
        {paginated.length > 0 ? (
          paginated.map((appointment, index) => (
            <AppointmentCard
              key={appointment.id}
              appointment={appointment}
              index={page * pageSize + index}
              onView={handleViewAppointment}
              onComplete={(id) => handleStatusUpdate(id, 'COMPLETED')}
              onCancel={(id) => handleStatusUpdate(id, 'CANCELLED')}
              onOpenNotes={handleOpenNotesModal}
              onOpenPrescription={handleOpenPrescriptionModal}
              selected={selectedIds.has(appointment.id)}
              onToggleSelect={toggleSelect}
            />
          ))
        ) : (
          <Card sx={{ p: 6, textAlign: 'center', border: '1px solid', borderColor: 'grey.100', boxShadow: 'none', borderRadius: 3 }}>
            <CalendarOutlined style={{ fontSize: 40, color: '#CBD5E1' }} />
            <Typography variant="body1" sx={{ mt: 2, color: '#94A3B8', fontWeight: 500 }}>
              {search ? 'No appointments match your search' : 'No appointments found'}
            </Typography>
          </Card>
        )}
      </Stack>

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2, px: 1 }}>
          <Typography variant="caption" sx={{ color: '#94A3B8' }}>
            Showing {totalCount === 0 ? 0 : page * pageSize + 1}–{Math.min((page + 1) * pageSize, totalCount)} of {totalCount}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="outlined" disabled={page === 0}
              onClick={() => setPage(p => p - 1)}
              sx={{ textTransform: 'none', borderColor: '#E2E8F0', color: '#64748B', borderRadius: 1.5, minWidth: 80 }}>
              Previous
            </Button>
            <Button size="small" variant="outlined" disabled={page >= totalPages - 1}
              onClick={() => setPage(p => p + 1)}
              sx={{ textTransform: 'none', borderColor: '#E2E8F0', color: '#64748B', borderRadius: 1.5, minWidth: 80 }}>
              Next
            </Button>
          </Stack>
        </Stack>
      )}

      {/* ── Modals ── */}

      {/* Appointment Details */}
      <AppointmentDetailsModal open={modalOpen} onClose={() => { setModalOpen(false); setSelectedAppointment(null); }} data={selectedAppointment} />

      {/* Book Appointment */}
      <AdminBookAppointmentModal open={bookModalOpen} onClose={() => setBookModalOpen(false)} onSuccess={fetchAppointments} />

      {/* Bulk Delete Confirmation */}
      <Modal open={deleteConfirmOpen} onClose={() => !bulkDeleting && setDeleteConfirmOpen(false)}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 420 }, bgcolor: 'background.paper', borderRadius: 3, boxShadow: 24, overflow: 'hidden',
        }}>
          <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: '1px solid', borderColor: 'grey.100' }}>
            <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: '#FEF2F2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ExclamationCircleOutlined style={{ color: '#B91C1C', fontSize: 18 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Delete Appointments?</Typography>
          </Box>
          <Box sx={{ px: 3, py: 3 }}>
            <Typography variant="body2" sx={{ color: '#475569', lineHeight: 1.6 }}>
              You are about to permanently delete <strong>{selectedIds.size}</strong> appointment{selectedIds.size === 1 ? '' : 's'}. This action cannot be undone.
            </Typography>
          </Box>
          <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'flex-end', gap: 1.5, borderTop: '1px solid', borderColor: 'grey.100' }}>
            <Button variant="outlined" onClick={() => setDeleteConfirmOpen(false)} disabled={bulkDeleting}
              sx={{ borderRadius: 2, textTransform: 'none', borderColor: '#E2E8F0', color: '#64748B' }}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleBulkDelete} disabled={bulkDeleting}
              sx={{ borderRadius: 2, textTransform: 'none', bgcolor: '#B91C1C', '&:hover': { bgcolor: '#991B1B' }, boxShadow: 'none' }}>
              {bulkDeleting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : `Delete ${selectedIds.size}`}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Notes Modal */}
      <Modal open={notesModalOpen} onClose={handleCloseNotesModal}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 440 }, bgcolor: 'background.paper', borderRadius: 3, boxShadow: 24, p: 0, overflow: 'hidden',
        }}>
          <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid', borderColor: 'grey.100' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{isEditingNote ? 'Edit Note' : 'Add Note'}</Typography>
            <IconButton onClick={handleCloseNotesModal} size="small"><CloseOutlined /></IconButton>
          </Box>
          <Box sx={{ p: 3 }}>
            <TextField fullWidth multiline rows={4} placeholder="Enter your note here..." value={notesInput}
              onChange={(e) => setNotesInput(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Box>
          <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'flex-end', gap: 1.5, borderTop: '1px solid', borderColor: 'grey.100' }}>
            <Button variant="outlined" onClick={handleCloseNotesModal}
              sx={{ borderRadius: 2, textTransform: 'none', borderColor: '#E2E8F0', color: '#64748B' }}>Cancel</Button>
            <Button variant="contained" onClick={handleSubmitNotes} disabled={notesLoading || !notesInput.trim()}
              sx={{ borderRadius: 2, textTransform: 'none', bgcolor: '#0D9488', '&:hover': { bgcolor: '#0F766E' } }}>
              {notesLoading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Save Note'}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Prescription Modal */}
      <Modal open={prescriptionModalOpen} onClose={handleClosePrescriptionModal}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 480 }, bgcolor: 'background.paper', borderRadius: 3, boxShadow: 24, p: 0, overflow: 'hidden',
        }}>
          <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid', borderColor: 'grey.100' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{isEditingPrescription ? 'Edit Prescription' : 'Add Prescription'}</Typography>
            <IconButton onClick={handleClosePrescriptionModal} size="small"><CloseOutlined /></IconButton>
          </Box>
          <Box sx={{ p: 3 }}>
            <TextField fullWidth multiline rows={4} placeholder="Enter prescription details…" value={prescriptionInput}
              onChange={(e) => setPrescriptionInput(e.target.value)}
              sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />

            {existingPrescriptionFile && (
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, border: '1px solid #DCFCE7', borderRadius: 2, bgcolor: '#F0FDF4' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DownloadOutlined style={{ color: '#15803D' }} />
                  <Typography variant="body2" sx={{ color: '#15803D', fontWeight: 500 }}>Existing file</Typography>
                </Box>
                <Button size="small" startIcon={<DownloadOutlined />} onClick={() => window.open(existingPrescriptionFile, '_blank')}
                  sx={{ textTransform: 'none', color: '#15803D' }}>Download</Button>
              </Box>
            )}

            <Typography variant="caption" sx={{ color: '#94A3B8', mb: 1, display: 'block' }}>
              Attachment (PDF, PNG, JPG — Max 10MB)
            </Typography>
            {!attachmentFile ? (
              <Button variant="outlined" component="label" startIcon={<CloudUploadOutlined />} fullWidth
                sx={{ py: 1.5, borderStyle: 'dashed', borderColor: '#E2E8F0', color: '#64748B', textTransform: 'none', borderRadius: 2 }}>
                Upload File
                <input type="file" hidden accept=".pdf,.png,.jpg,.jpeg" onChange={handleAttachmentChange} />
              </Button>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, border: '1px solid', borderColor: 'grey.200', borderRadius: 2, bgcolor: '#F8FAFC' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
                  <CloudUploadOutlined style={{ color: '#0D9488' }} />
                  <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>
                    {attachmentFile.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({(attachmentFile.size / (1024 * 1024)).toFixed(2)} MB)
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => { setAttachmentFile(null); setAttachmentError(''); }} color="error">
                  <DeleteOutlined />
                </IconButton>
              </Box>
            )}
            {attachmentError && <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{attachmentError}</Typography>}
          </Box>
          <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'flex-end', gap: 1.5, borderTop: '1px solid', borderColor: 'grey.100' }}>
            <Button variant="outlined" onClick={handleClosePrescriptionModal}
              sx={{ borderRadius: 2, textTransform: 'none', borderColor: '#E2E8F0', color: '#64748B' }}>Cancel</Button>
            <Button variant="contained" onClick={handleSubmitPrescription} disabled={prescriptionLoading || !prescriptionInput.trim()}
              sx={{ borderRadius: 2, textTransform: 'none', bgcolor: '#0D9488', '&:hover': { bgcolor: '#0F766E' } }}>
              {prescriptionLoading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Save Prescription'}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}
